import { Component, signal, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { auditTime, combineLatest } from 'rxjs';
import { ReaderShellComponent, ReaderDocument } from 'web-reader-lib';
import { environment } from '../../../../environments/environment'
import { version, build } from '../../../../buildVersion'
import { AppCheckService } from './services/app-check.service';
import { AnalyticsService } from './services/analytics.service';
import { ShareFabComponent } from './share-fab.component';
import { ReaderThemeCacheService } from './services/reader-theme-cache.service';
@Component({
  selector: 'app-reader-page',
  standalone: true,
  imports: [CommonModule, ReaderShellComponent, ShareFabComponent],
  template: `
    <div class="mx-auto min-h-[90vh] w-full lg:max-w-[500px]">

      @if (isDraftPreview()) {
        <div class="bg-amber-100 px-4 py-3 text-center text-sm font-medium text-amber-950" role="status">
          {{ draftPreviewMessage }}
        </div>
      }

      @if (!doc() && !error()) {
        <div class="w-100 mx-auto h-100 flex flex-col justify-center text-center">
            <div class="animate-spin h-30 mx-auto w-30 border-b-5 border-orange-500 rounded-full"></div>
            <div class="text-center w-100 my-4 text-gray-500">Loading…</div>
        </div>
      }
      @if (error()) {
        <div class="text-red-600">Error: {{ error() }}</div>
      }

      @if (doc()) {
        <wr-reader-shell
          [document]="doc()!"
          (formPrepared)="onFormPrepared($event)"
        ></wr-reader-shell>
      }

    </div>
    <div class="text-gray-400 text-xs text-center w-full p-2">Narravero Web Reader - {{ this.version }} <span *ngIf="this.hostname != 'api.narravero.com'">(#{{ this.build }})</span></div>
  
    <app-share-fab [doc]="doc()" [draftPreview]="isDraftPreview()"></app-share-fab>
    @if (toastVisible) {
      <div class="fixed inset-x-0 bottom-4 flex justify-center z-50 pointer-events-none">
        <div
          class="pointer-events-auto px-4 py-2 rounded-full text-sm shadow-lg bg-neutral-900 text-white flex items-center gap-2 transition-all duration-200 transform opacity-100 translate-y-0"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
          <span>{{ toastMessage }}</span>
        </div>
      </div>
    }

  `,
})

export class ReaderPageComponent implements AfterViewInit, OnDestroy {

  private ticking = false;
  private lastSentStep = 0;
  private pendingStep: number | null = null;
  private debounceTimer: any = null;
  private lastSendTime = 0;
  private isTabActive = true;

  private readonly DEBOUNCE_MS = 350;
  private readonly MIN_INTERVAL = 1200;

  private formReloadTimeout: any = null;
  toastVisible = false;
  toastMessage = '';
  private toastTimeout: any = null;

  doc = signal<ReaderDocument | null>(null);
  error = signal<string | null>(null);
  isContentLoading = signal(false);
  isDraftPreview = signal(false);
  draftPreviewMessage = (navigator.language || 'en').toLowerCase().startsWith('de')
    ? 'Entwurfsvorschau – diese Version ist noch nicht veröffentlicht.'
    : 'Draft preview — this version is not published yet.';

  version = version;
  build = build;
  hostname = environment.urlHostName;

  private navigationStack: { prefix: string | null; passId: string; childId?: string | null }[] = [];
  private currentPrefix: string | null = null;
  private currentPassId: string | null = null;
  private currentChildId: string | null = null;
  private currentDraftId: string | null = null;
  private contentRequestId = 0;
  private pendingRestoreScrollY: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private appCheck: AppCheckService,
    private analytics: AnalyticsService,
    private themeCache: ReaderThemeCacheService,
  ) {

    combineLatest([this.route.paramMap, this.route.queryParamMap]).pipe(auditTime(0)).subscribe(([params, queryParams]) => {
      const prefix = params.get('prefix');   // may be null
      const dppId = this.resolveRoutePassId(params); // main DPP/pass
      const childId = params.get('childId'); // optional child
      const draftId = queryParams.get('draftId') || queryParams.get('draft') || null;

      if (!dppId) {
        this.error.set('No ID in URL');
        return;
      }

      this.currentPrefix = prefix;
      this.currentPassId = dppId;
      this.currentChildId = childId;
      this.currentDraftId = draftId;
      this.isDraftPreview.set(!!draftId);
      const requestId = ++this.contentRequestId;

      if (childId) {
        this.pendingRestoreScrollY = null;
        this.fetchChildDocument(childId, draftId, requestId);
      } else {
        this.pendingRestoreScrollY = this.getSavedScrollY();
        this.fetchDocument(dppId, draftId, requestId);
      }

      window.addEventListener('beforeunload', this.handleBeforeUnload);
      window.addEventListener('pagehide', this.handleBeforeUnload);

    });

    // Add link click listener
    document.addEventListener('click', this.handleDocumentClick, true);
  }

  ngOnDestroy() {
    document.removeEventListener('click', this.handleDocumentClick, true);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    window.removeEventListener('pagehide', this.handleBeforeUnload);
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('resize', this.onResize);

    if (this.formReloadTimeout) {
      clearTimeout(this.formReloadTimeout);
      this.formReloadTimeout = null;
    }
  }

  private getScrollPercent(): number {
    // Measure page scroll (window)
    const scrollTop =
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    const scrollHeight =
      document.documentElement.scrollHeight ||
      document.body.scrollHeight ||
      0;

    const clientHeight = window.innerHeight || document.documentElement.clientHeight || 0;

    const total = scrollHeight - clientHeight;
    if (total <= 0) return 100;

    const pct = (scrollTop / total) * 100;
    return Math.max(0, Math.min(100, Math.round(pct)));
  }

  private maybeQueueStep() {
    const pct = this.getScrollPercent();
    const step = Math.floor(pct / 5) * 5;
    if (step <= this.lastSentStep) return;

    this.pendingStep = step;

    // 100% — send right away
    if (this.pendingStep === 100) {
      this.flushPending(true);
      return;
    }

    // wait until active
    if (!this.isTabActive) return;

    // debounce
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.flushPending(), this.DEBOUNCE_MS);
  }

  private async flushPending(force = false) {
    if (this.pendingStep == null) return;

    if (!force && !this.isTabActive) return;

    const now = Date.now();
    const elapsed = now - this.lastSendTime;

    if (!force && elapsed < this.MIN_INTERVAL) {
      const wait = this.MIN_INTERVAL - elapsed;
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.flushPending(force), wait);
      return;
    }

    const doc = this.doc();
    if (doc) {
      await this.analytics.trackPassViewed(doc, this.pendingStep);
    }

    this.lastSentStep = this.pendingStep;
    this.lastSendTime = now;
    this.pendingStep = null;

  }

  private onScroll = () => {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.maybeQueueStep();
      this.ticking = false;
    });
  };

  private onVisibilityChange = () => {
    const nowVisible = document.visibilityState === 'visible';
    this.isTabActive = nowVisible;

    if (!nowVisible) {
      this.flushPending(true);
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    } else {
      this.maybeQueueStep();
    }
  };

  private onResize = () => {
    this.maybeQueueStep();
  };

  async onFormPrepared(widgetPayload: any) {

    const currentDoc = this.doc();

    if (widgetPayload.reloadDPP) {
      this.showToast(this.toastText('reload'), 5000);
    }

    const postData: any = {
      contextId: (currentDoc as any)?.contextId ?? null,
      data: widgetPayload.data,
      passTemplateId: (currentDoc as any)?.passTemplateId ?? null,
      widgetId: widgetPayload.widgetId,
      timeStamp: widgetPayload.timeStamp ?? new Date().toISOString(),
      async: widgetPayload.reloadDPP === true,
      tridyId: (currentDoc as any)?.tridyId ?? null,
    };

    if (widgetPayload.workflowTemplateId) {
      postData.workflowTemplateId = widgetPayload.workflowTemplateId;
    }

    const url = `https://${this.hostname}/v1/form`;
    const token = await this.appCheck.getToken();

    let headers = new HttpHeaders({
      'Content-Type': 'application/json;charset=UTF-8',
    });

    const ctx = postData.contextId;
    if (ctx) {
      headers = headers.set('Context-Id', String(ctx));
    }

    if (token) {
      headers = headers.set('X-Firebase-AppCheck', token);
    } else {
      console.warn('No App Check token!');
    }

    //console.log('POST /v1/form – will send payload:', postData);
    //console.log('POST /v1/form – headers:', headers);

    this.http.post(url, postData, { headers }).subscribe({
      next: (resp) => {
        console.log('Form submitted successfully:', resp);

        if (widgetPayload.reloadDPP) {
          if (this.formReloadTimeout) {
            clearTimeout(this.formReloadTimeout);
          }

          this.formReloadTimeout = setTimeout(() => {
            if (this.currentPassId) {
              if (this.currentChildId) {
                this.fetchChildDocument(this.currentChildId, this.currentDraftId);
              } else {
                this.fetchDocument(this.currentPassId, this.currentDraftId);
              }
            } else {
              window.location.reload();
            }

            this.formReloadTimeout = null;
          }, 5000);
        }
      },
      error: (err) => {
        console.error('Failed to submit form:', err);
        if (this.formReloadTimeout) {
          clearTimeout(this.formReloadTimeout);
          this.formReloadTimeout = null;
        }
      },
    });

  }
  
  private fetchDocument(
    id: string,
    draftId: string | null = this.currentDraftId,
    requestId = ++this.contentRequestId,
  ) {
    this.error.set(null);
    this.doc.set(null);

    const url = `https://${this.hostname}/v1/pass/${this.toPassApiPath(id)}`;

    this.http.get<ReaderDocument>(url, { params: this.passQueryParams(draftId) }).subscribe({
      next: (data) => {
        if (requestId !== this.contentRequestId) return;
        const rewritten = this.rewriteMediaUrls(
          data as ReaderDocument & { passTemplateId?: string | null; contextId?: string | null },
          { hostname: this.hostname }
        );

        // cache main theme for this pass
        if (!draftId) this.themeCache.set(this.currentPrefix, id, rewritten.theme ?? null);

        //console.log('Fetched document:', rewritten);
        this.doc.set(rewritten);
        this.restorePendingScrollPosition();
        this.analytics.trackPassOpened(rewritten);
      },
      error: (err) => {
        if (requestId !== this.contentRequestId) return;
        console.error(err);
        this.error.set('Failed to load pass');
      },
    });
  }

  ngAfterViewInit() {

    window.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('resize', this.onResize);
  }

  private fetchChildDocument(
    childId: string,
    draftId: string | null = this.currentDraftId,
    requestId = ++this.contentRequestId,
  ) {
    if (!this.currentPassId) return;

    this.error.set(null);
    this.doc.set(null);

    const passId = this.currentPassId;
    const prefix = this.currentPrefix;

    const mainTheme = draftId ? null : this.themeCache.get(prefix, passId);
    if (mainTheme) {
      return this.fetchChildWithForcedTheme(passId, childId, mainTheme, draftId, requestId);
    }

    const mainUrl = `https://${this.hostname}/v1/pass/${this.toPassApiPath(passId)}`;
    this.http.get<ReaderDocument>(mainUrl, { params: this.passQueryParams(draftId) }).subscribe({
      next: (mainDoc) => {
        if (requestId !== this.contentRequestId) return;
        const mainRewritten = this.rewriteMediaUrls(mainDoc as any, { hostname: this.hostname });
        const theme = mainRewritten.theme ?? null;

        if (theme && !draftId) this.themeCache.set(prefix, passId, theme);

        // now load child using forced main theme (even if null)
        this.fetchChildWithForcedTheme(passId, childId, theme, draftId, requestId);
      },
      error: (err) => {
        if (requestId !== this.contentRequestId) return;
        console.error(err);
        this.fetchChildWithForcedTheme(passId, childId, null, draftId, requestId);
      },
    });
  }

  private fetchChildWithForcedTheme(
    passId: string,
    childId: string,
    forcedTheme: any | null,
    draftId: string | null,
    requestId: number,
  ) {
    const url = `https://${this.hostname}/v1/pass/${this.toPassApiPath(passId)}/child/${encodeURIComponent(childId)}`;

    this.http.get<ReaderDocument>(url, { params: this.passQueryParams(draftId) }).subscribe({
      next: (childDoc) => {
        if (requestId !== this.contentRequestId) return;
        const rewritten = this.rewriteMediaUrls(childDoc as any, { hostname: this.hostname });

        const merged: ReaderDocument = {
          ...rewritten,
          theme: forcedTheme ?? rewritten.theme,
        };

        this.doc.set(merged);
        this.analytics.trackPassOpened(merged);
      },
      error: (err) => {
        if (requestId !== this.contentRequestId) return;
        console.error(err);
        this.error.set('Failed to load pass');
      },
    });
  }

  // Navigation helpers

  private resolveRoutePassId(params: ParamMap): string | null {
    const gs1Root = params.get('gs1Root');
    const gtin = params.get('gtin');

    if (gs1Root && gtin) {
      return gs1Root;
    }

    return params.get('dppId');
  }

  private toPassApiPath(passId: string): string {
    return encodeURIComponent(passId);
  }

  private passQueryParams(draftId: string | null): { draftId: string } | undefined {
    return draftId ? { draftId } : undefined;
  }

  private passIdRouteSegments(passId: string): string[] {
    return [passId];
  }

  private buildRouteCommands(prefix: string | null, passId: string, childId?: string | null) {
    const passIdSegments = this.passIdRouteSegments(passId);

    if (childId) {
      // with child
      return prefix
        ? ['/', prefix, ...passIdSegments, 'child', childId]
        : ['/', ...passIdSegments, 'child', childId];
    } else {
      // root dpp
      return prefix
        ? ['/', prefix, ...passIdSegments]
        : ['/', ...passIdSegments];
    }
  }

  private pushCurrentToStack() {
    if (!this.currentPassId) return;
    this.saveCurrentScrollY();
    this.navigationStack.push({
      prefix: this.currentPrefix,
      passId: this.currentPassId,
      childId: this.currentChildId,
    });
    //this.showBackButton.set(true);
  }

  // Navigation from links
  private openChild(childId: string) {
    if (!this.currentPassId) return;

    this.pushCurrentToStack();

    this.currentChildId = childId;
    const commands = this.buildRouteCommands(this.currentPrefix, this.currentPassId, childId);
    this.router.navigate(commands, {
      state: { returnScrollY: this.getSavedScrollY() },
      queryParamsHandling: 'preserve',
    });
  }

  private openDpp(passId: string) {
    if (this.currentPassId) {
      this.pushCurrentToStack();
    }

    this.currentPassId = passId;
    this.currentChildId = null;

    const commands = this.buildRouteCommands(this.currentPrefix, passId);
    this.router.navigate(commands);
  }

  // Link click interception

  private handleBeforeUnload = () => {
    const currentDoc = this.doc();
    if (currentDoc) {
      this.analytics.trackPassClosed(currentDoc);
    }
  };

  private handleDocumentClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    const anchor = target.closest('a') as HTMLAnchorElement | null;
    if (!anchor) return;

    const rawHref = anchor.getAttribute('href');
    if (!rawHref) return;

    const currentDoc = this.doc();
    if (currentDoc) {
      this.analytics.trackLinkOpened(currentDoc, anchor.href);
    }

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    let href = rawHref.trim();
    if (href.startsWith('https://#') || href.startsWith('http://#')) {
      href = href.replace(/^https?:\/\//, '');
    }

    if (href === 'dpp://main') {
      event.preventDefault();

      if (this.currentPassId) {
        this.pushCurrentToStack();

        this.currentChildId = null;

        const returnScrollY = this.getSavedScrollY('returnScrollY');
        const commands = this.buildRouteCommands(
          this.currentPrefix,
          this.currentPassId,
          null
        );

        this.router.navigate(
          commands,
          {
            queryParamsHandling: 'preserve',
            ...(returnScrollY == null ? {} : { state: { scrollY: returnScrollY } }),
          }
        );
      }

      return;
    }

    if (href.startsWith('child:')) {
      event.preventDefault();
      const childId = href.substring('child:'.length);
      if (childId) this.openChild(childId);
      return;
    }

    if (href.startsWith('dpp:')) {
      event.preventDefault();
      const passId = href.substring('dpp:'.length);
      if (passId) this.openDpp(passId);
      return;
    }

    if (href.startsWith('#')) {
      event.preventDefault();
      const widgetId = href.slice(1);
      if (widgetId) this.scrollToWidget(widgetId);
      return;
    }

    const host = anchor.hostname;
    if (this.isDppHost(host)) {
      event.preventDefault();
      const path = anchor.pathname || '/';
      const tridyId = path.replace(/^\//, '');
      if (tridyId) this.openDpp(tridyId);
      return;
    }
  };

  private isDppHost(hostname: string): boolean {
    return (
      hostname === 'nv2.at' ||
      hostname === 'nvtest.eu' ||
      hostname === 'nvero.app'
    );
  }

  private scrollToWidget(widgetId: string) {
    const el = document.getElementById(widgetId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private saveCurrentScrollY() {
    const scrollY = window.scrollY || window.pageYOffset || 0;
    window.history.replaceState(
      {
        ...(window.history.state ?? {}),
        scrollY,
      },
      '',
      window.location.href
    );
  }

  private getSavedScrollY(key: 'scrollY' | 'returnScrollY' = 'scrollY'): number | null {
    const scrollY = window.history.state?.[key];
    return typeof scrollY === 'number' ? scrollY : null;
  }

  private restorePendingScrollPosition() {
    const scrollY = this.pendingRestoreScrollY;
    if (scrollY == null) return;

    this.pendingRestoreScrollY = null;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY, behavior: 'auto' });
      });
    });
  }

  rewriteMediaUrls(
    doc: ReaderDocument & { passTemplateId?: string | null; contextId?: string | null },
    opts: { hostname: string }
  ): ReaderDocument {
    const base = `https://${opts.hostname}`;
    const passTemplateId = doc.passTemplateId ?? null;
    const contextId = doc.contextId ?? null;

    const rewriteUrl = (url?: string | null): string | undefined => {
      if (url == null) return undefined; // normalize null -> undefined

      // pass://filename.ext  →  https://host/v1/pass/:passTemplateId/filename.ext
      if (url.startsWith('pass://')) {
        const file = url.slice('pass://'.length);
        if (!file || !passTemplateId) return undefined;
        return `${base}/v1/pass/${passTemplateId}/${file}`;
      }

      // if existing, backend includes media directory in the url
      if (url.startsWith('context://')) {
        const file = url.slice('context://'.length);
        if (!file || !contextId) return undefined;
        return `${base}/v1/context/${contextId}/${file}`;
      }

      return url;
    };

    const widgets = (doc.widgets ?? []).map((w: any) => {
      const copy = { ...w };

      switch (copy.type) {
        case 'image':
          copy.url = rewriteUrl(copy.url);
          copy.fallbackUrl = rewriteUrl(copy.fallbackUrl);
          copy.linkURL = rewriteUrl(copy.linkURL);
          break;

        case 'images':
        case 'imagesHorizontal':
        case 'imageSlider':
          if (Array.isArray(copy.images)) {
            copy.images = copy.images.map((img: any) => ({
              ...img,
              url: rewriteUrl(img.url),
              fallbackUrl: rewriteUrl(img.fallbackUrl),
              linkURL: rewriteUrl(img.linkURL),
            }));
          }
          break;

        case 'billboardImage':
          copy.url = rewriteUrl(copy.url);
          copy.fallbackUrl = rewriteUrl(copy.fallbackUrl);
          copy.linkURL = rewriteUrl(copy.linkURL);
          break;

        case 'video':
        case 'billboardVideo':
        case 'splashVideo':
          copy.url = rewriteUrl(copy.url);
          copy.coverImageURL = rewriteUrl(copy.coverImageURL);
          break;

        case 'callToAction':
          copy.imageUrl = rewriteUrl(copy.imageUrl);
          copy.fallbackUrl = rewriteUrl(copy.fallbackUrl);
          if (Array.isArray(copy.socialNetworks)) {
            copy.socialNetworks = copy.socialNetworks.map((s: any) => ({
              ...s,
              url: rewriteUrl(s.url),
            }));
          }
          break;

        case 'annotatedMap':
          if (Array.isArray(copy.annotations)) {
            copy.annotations = copy.annotations.map((a: any) => ({
              ...a,
              linkURL: rewriteUrl(a.linkURL),
            }));
          }
          break;
      }
      // TODO: Text? Social Media?
      return copy;
    });

    return {
      ...doc,
      widgets,
    };
  }

  // TOAST
private showToast(message: string, duration?: number) {
  this.toastMessage = message;
  this.toastVisible = true;

  if (this.toastTimeout) {
    clearTimeout(this.toastTimeout);
    this.toastTimeout = null;
  }

  if (duration && duration > 0) {
    this.toastTimeout = setTimeout(() => {
      this.toastVisible = false;
      this.toastTimeout = null;
    }, duration);
  }
}

  private toastText(key: 'reload' | 'success'): string {
    const lang = (navigator.language || 'en').toLowerCase();
    const de = lang.startsWith('de');

    switch (key) {
      case 'reload':
        return de
          ? 'Erfolgreich gesendet. Wird neu geladen…'
          : 'Success! Reloading…';

      case 'success':
        return de
          ? 'Formular erfolgreich gesendet.'
          : 'Form submitted successfully.';

      default:
        return '';
    }
  }

}
